import { useQuery } from '@tanstack/react-query';
import { customInstance } from '@edison/api-client-react';
import { useAuthStore } from '@/store/auth-store.js';
import { SkeletonCard } from '@/components/ui/skeleton.js';
import { InlineError } from '@/components/ui/inline-error.js';

interface MachineRegistration {
  id: string;
  label: string;
  hookCurrent: boolean;
  revokedAt: string | null;
  lastSeenAt: string | null;
}

interface MachineListResponse {
  data: {
    items: MachineRegistration[];
  };
}

export function DeveloperMySetupPage() {
  const { user } = useAuthStore();

  const { data, isLoading, error, refetch } = useQuery<MachineListResponse>({
    queryKey: ['machine-registrations'],
    queryFn: () =>
      customInstance<MachineListResponse>({
        url: '/api/machine-registrations',
        method: 'GET',
      }),
    retry: false,
  });

  const items = data?.data?.items ?? [];

  return (
    <div className="max-w-[1320px] mx-auto px-8 py-6 lg:px-10 animate-page">
      <div className="mb-8">
        <h1 className="text-[22px] font-semibold tracking-tight text-foreground mb-1">My Setup</h1>
        <p className="text-sm text-muted-foreground">Configure your machine for Edison capture</p>
      </div>

      {/* Hook install instructions */}
      <section className="rounded-[14px] border border-border bg-card px-6 py-5 mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-3">Claude Code hook</h2>
        <p className="text-sm text-muted-foreground mb-4">
          Run this from your Edison repo to install or update the capture hook:
        </p>
        <pre className="bg-[oklch(0.97_0.004_250)] dark:bg-[oklch(0.18_0.004_250)] rounded-[9px] px-4 py-3 text-xs font-mono text-foreground overflow-x-auto">
          scripts/hooks/install.sh
        </pre>
        <p className="text-xs text-muted-foreground mt-3">
          Get your machine key from{' '}
          <span className="font-medium text-foreground">Settings → Developers → Machine Keys</span>{' '}
          and set it as <code className="font-mono">EDISON_KEY</code> in{' '}
          <code className="font-mono">~/.config/edison/config</code>.
        </p>
      </section>

      {/* Registered machines */}
      <section className="mb-6">
        <h2 className="text-sm font-semibold text-foreground mb-3">Registered machines</h2>
        {isLoading ? (
          <SkeletonCard />
        ) : error ? (
          <InlineError message="Failed to load machines." onRetry={() => void refetch()} />
        ) : (
          <div className="rounded-[14px] border border-border bg-card divide-y divide-border">
            {items.length === 0 ? (
              <div className="px-5 py-4 text-sm text-muted-foreground">
                No machines registered yet. Run the install script above to get started.
              </div>
            ) : (
              items.map((m) => (
                <div key={m.id} className="flex items-center gap-4 px-4 py-3">
                  <div
                    className={`h-2 w-2 rounded-full shrink-0 ${
                      m.revokedAt
                        ? 'bg-[oklch(0.65_0.10_25)]'
                        : m.hookCurrent
                        ? 'bg-[oklch(0.55_0.14_155)]'
                        : 'bg-[oklch(0.65_0.14_65)]'
                    }`}
                    aria-hidden="true"
                  />
                  <span className="flex-1 text-sm font-mono text-foreground">{m.label}</span>
                  <span className="text-xs text-muted-foreground">
                    {m.revokedAt
                      ? 'Revoked'
                      : m.hookCurrent
                      ? 'Hook up to date'
                      : 'Hook outdated — re-run install.sh'}
                  </span>
                  {m.lastSeenAt && (
                    <span className="text-xs text-muted-foreground font-mono">
                      {new Date(m.lastSeenAt).toLocaleDateString('en-AU', {
                        day: 'numeric',
                        month: 'short',
                      })}
                    </span>
                  )}
                </div>
              ))
            )}
          </div>
        )}
      </section>

      {/* Identity */}
      <section className="rounded-[14px] border border-border bg-card px-6 py-5">
        <h2 className="text-sm font-semibold text-foreground mb-3">Identity</h2>
        <div className="space-y-1">
          <p className="text-sm font-medium text-foreground">{user?.name ?? '—'}</p>
          <p className="text-xs text-muted-foreground">{user?.email ?? '—'}</p>
        </div>
      </section>
    </div>
  );
}
